import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProspects, createProspect, getUsers, deleteProspect } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Upload, Search, Download, Trash2, Phone, Mail, MapPin, User, UserPlus, Calendar } from 'lucide-react';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useActionHistory } from '@/context/ActionHistoryContext';
import { safeToastError } from '@/utils/errorUtils';
import { getValidToken, handleTokenError } from '@/utils/tokenUtils';
import PageHeader from '@/components/PageHeader';

const Customers = ({ user }) => {
  const navigate = useNavigate();
  const [prospects, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedRepresentative, setSelectedRepresentative] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date-desc'); // Varsayılan: en yeni en üstte
  const { pushAction } = useActionHistory();
  const itemsPerPage = 100;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    district: '',
    contact_person: '',
    representative: '',
    device_count: '',
    tax_number: '',
    tax_office: ''
  });

  useEffect(() => {
    loadProspects();
    loadUsers();
  }, []);

  // Benzersiz şehir listesi
  const uniqueCities = useMemo(() => {
    const cities = prospects
      .map(c => c.city)
      .filter(city => city && city.trim() !== '');
    return [...new Set(cities)].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [prospects]);

  const filteredCustomers = useMemo(() => {
    let filtered = prospects.filter(prospect => {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        prospect.name?.toLowerCase().startsWith(query) ||
        prospect.city?.toLowerCase().startsWith(query) ||
        prospect.district?.toLowerCase().startsWith(query) ||
        prospect.representative?.toLowerCase().startsWith(query) ||
        prospect.phone?.startsWith(searchQuery) ||
        prospect.contact_person?.toLowerCase().startsWith(query) ||
        prospect.address?.toLowerCase().startsWith(query)
      );
      const matchesCity = selectedCity === 'all' || prospect.city === selectedCity;
      const matchesRepresentative = selectedRepresentative === 'all' || prospect.representative === selectedRepresentative;
      
      return matchesSearch && matchesCity && matchesRepresentative;
    });
    
    return filtered.sort((a, b) => {
      switch(sortBy) {
        case 'date-desc':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'date-asc':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'name-asc':
          return a.name.localeCompare(b.name, 'tr');
        case 'name-desc':
          return b.name.localeCompare(a.name, 'tr');
        case 'city-asc':
          return (a.city || '').localeCompare(b.city || '', 'tr');
        case 'city-desc':
          return (b.city || '').localeCompare(a.city || '', 'tr');
        case 'arvento-desc':
          return (b.device_count || 0) - (a.device_count || 0);
        case 'arvento-asc':
          return (a.device_count || 0) - (b.device_count || 0);
        case 'representative-asc':
          return (a.representative || '').localeCompare(b.representative || '', 'tr');
        case 'representative-desc':
          return (b.representative || '').localeCompare(a.representative || '', 'tr');
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });
  }, [prospects, searchQuery, sortBy, selectedCity, selectedRepresentative]);

  // Reset to page 1 when search query or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy, selectedCity, selectedRepresentative]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  const loadProspects = async () => {
    try {
      const data = await getProspects();
      setCustomers(data);
    } catch (error) {
      console.error('Customer load error:', error);
      // Handle token errors specially
      try {
        handleTokenError(error);
      } catch (tokenError) {
        toast.error(safeToastError(tokenError));
        return;
      }
      toast.error(safeToastError(error));
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Users load error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Boş numeric alanları 0 yap
      const dataToSubmit = {
        ...formData,
        device_count: formData.device_count === '' ? 0 : parseInt(formData.device_count) || 0
      };
      await createProspect(dataToSubmit);
      toast.success('Müşteri Adayı başarıyla eklendi');
      setShowAddDialog(false);
      loadProspects();
      setFormData({
        name: '',
        phone: '',
        email: '',
        city: '',
        district: '',
        contact_person: '',
        representative: '',
        device_count: ''
      });
    } catch (error) {
      console.error('Prospect create error:', error);
      toast.error('Müşteri Adayı eklenirken hata oluştu: ' + (error.response?.data?.detail?.[0]?.msg || error.message));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await bulkImportCustomers(file);
      // Backend'den gelen response: { prospects_added, prospects_skipped, total_errors, message, errors, success }
      const message = result.message || `${result.prospects_added || 0} müşteri başarıyla içe aktarıldı`;
      toast.success(message);
      setShowImportDialog(false);
      loadProspects();
    } catch (error) {
      console.error('Import error:', error);
      // Safe error handling to prevent React rendering issues
      const errorMessage = safeToastError(error);
      toast.error(errorMessage);
    }
  };

  const handleExport = () => {
    // Filtre kontrolü - herhangi bir filtre uygulanmış mı?
    const isFiltered = searchQuery.trim() !== '' || 
                       selectedCity !== 'all' || 
                       selectedRepresentative !== 'all' ||
                       sortBy !== 'date-desc';
    
    // Filtre varsa filtrelenmiş veriyi, yoksa tüm veriyi kullan
    const dataToExport = isFiltered ? filteredCustomers : prospects;
    
    const exportData = dataToExport.map(prospect => ({
      'Tarih': prospect.created_at ? format(new Date(prospect.created_at), 'dd.MM.yyyy HH:mm', { locale: tr }) : '-',
      'Müşteri Adayı Adı': prospect.name,
      'Yetkili Kişi': prospect.contact_person || '',
      'Telefon': prospect.phone || '',
      'E-posta': prospect.email || '',
      'Adres': prospect.address || '',
      'Şehir': prospect.city || '',
      'İlçe': prospect.district || '',
      'Toplam Cihaz Adedi': prospect.device_count || 0,
      'Pasif Cihaz Adedi': prospect.inactive_device_count || 0,
      'Temsilci': prospect.representative || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Müşteri Adayları');
    
    const fileName = isFiltered 
      ? `musteri_adaylari_filtreli_${exportData.length}_adet.xlsx`
      : `tum_musteri_adaylari_${exportData.length}_adet.xlsx`;
    
    XLSX.writeFile(wb, fileName);
    toast.success(`${exportData.length} müşteri adayı Excel'e aktarıldı${isFiltered ? ' (filtrelenmiş)' : ' (tümü)'}`);
  };

  const handleDownloadSample = () => {
    const sampleData = [
      {
        'Müşteri Adayı Adı': 'ABC Lojistik A.Ş.',
        'Telefon': '5551234567',
        'E-posta': 'info@abclojistik.com',
        'Şehir': 'İstanbul',
        'İlçe': 'Kadıköy',
        'İletişim Kişisi': 'Ahmet Yılmaz',
        'Toplam Cihaz Adedi': 15,
        'Pasif Cihaz Adedi': 3,
        'Temsilci': 'Rengimdemirtas'
      },
      {
        'Müşteri Adayı Adı': 'XYZ Taşımacılık Ltd.',
        'Telefon': '5559876543',
        'E-posta': 'iletisim@xyztasimacilik.com',
        'Şehir': 'Ankara',
        'İlçe': 'Çankaya',
        'İletişim Kişisi': 'Fatma Demir',
        'Toplam Cihaz Adedi': 8,
        'Pasif Cihaz Adedi': 1,
        'Temsilci': 'Rengimdemirtas'
      },
      {
        'Müşteri Adayı Adı': 'DEF Kargo Hizmetleri',
        'Telefon': '5554567890',
        'E-posta': 'bilgi@defkargo.com.tr',
        'Şehir': 'İzmir',
        'İlçe': 'Konak',
        'İletişim Kişisi': 'Mehmet Özkan',
        'Toplam Cihaz Adedi': 25,
        'Pasif Cihaz Adedi': 5,
        'Temsilci': 'Rengimdemirtas'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Örnek Müşteri Adayı Adayları');
    XLSX.writeFile(wb, 'ornek_musteriler_sablon.xlsx');
    toast.success('Örnek Excel şablonu indirildi');
  };

  const handleDeleteCustomer = async (prospectId) => {
    if (!window.confirm('Bu müşteriyi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await deleteProspect(prospectId);
      toast.success('Müşteri Adayı silindi');
      loadProspects();
    } catch (error) {
      toast.error('Müşteri Adayı silinirken hata oluştu');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('TÜM müşterileri silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!')) return;
    if (!window.confirm('GERÇEKTEN tüm müşterileri silmek istiyor musunuz?')) return;
    
    try {
      await deleteAllCustomers();
      toast.success('Tüm müşteriler silindi');
      loadProspects();
    } catch (error) {
      toast.error('Müşteri Adayı Adayları silinirken hata oluştu');
    }
  };

  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Müşteri Adayı Adayları yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader 
        icon={UserPlus}
        title="Müşteri Adayları"
        subtitle={`Toplam ${filteredCustomers.length} müşteri adayı`}
      >
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (open) {
            // Dialog açıldığında formu sıfırla
            setFormData({
              name: '',
              phone: '',
              email: '',
              address: '',
              city: '',
              district: '',
              contact_person: '',
              representative: user?.role === 'temsilci' ? user?.username : '',
              device_count: '',
              tax_number: '',
              tax_office: ''
            });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-white text-blue-600 hover:bg-blue-50 shadow-md flex-1 sm:flex-none min-h-11">
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Yeni Müşteri Adayı</span>
              <span className="sm:hidden">Ekle</span>
            </Button>
          </DialogTrigger>
            <DialogContent className="max-w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
              <DialogHeader>
                <DialogTitle>Yeni Müşteri Adayı Ekle</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Müşteri Adayı Adı *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    type="tel"
                  />
                </div>
                <div>
                  <Label>E-posta</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    type="email"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Şehir</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>İlçe</Label>
                    <Input
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Adres</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Cadde, sokak, bina no..."
                  />
                </div>
                <div>
                  <Label>İletişim Kişisi</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Toplam Cihaz Adedi</Label>
                  <Input
                    value={formData.device_count}
                    onChange={(e) => setFormData({ ...formData, device_count: e.target.value })}
                    type="number"
                    min="0"
                    placeholder="Toplam cihaz sayısı"
                  />
                </div>
                {user?.role === 'admin' && (
                  <div>
                    <Label>Temsilci</Label>
                    <Select value={formData.representative} onValueChange={(value) => setFormData({ ...formData, representative: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Temsilci seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(u => (
                          <SelectItem key={u.username} value={u.username}>{u.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white min-h-11">
                  Müşteri Adayı Ekle
                </Button>
              </form>
            </DialogContent>
          </Dialog>

        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogTrigger asChild>
            <Button className="bg-white text-blue-600 hover:bg-blue-50 shadow-md flex-1 sm:flex-none min-h-11">
              <Upload className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">İçe Aktar</span>
              <span className="sm:hidden">Aktar</span>
            </Button>
          </DialogTrigger>
            <DialogContent className="max-w-full sm:max-w-md mx-4">
              <DialogHeader>
                <DialogTitle>Excel'den Müşteri Adayı İçe Aktarma</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  <strong>Sadece "Müşteri Adayı Adı" zorunludur.</strong> Diğer tüm sütunlar opsiyoneldir.
                </p>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <p className="font-semibold text-green-900">📥 Örnek Excel Şablonu</p>
                    <Button 
                      onClick={handleDownloadSample}
                      size="sm"
                      variant="outline"
                      className="text-green-700 border-green-300 hover:bg-green-100"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      İndir
                    </Button>
                  </div>
                  <p className="text-sm text-green-800">
                    Örnek veri ile hazır şablonu indirin, düzenleyin ve yükleyin.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-semibold text-blue-900 mb-2">📋 Desteklenen Sütunlar:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>• Müşteri Adayı Adı (zorunlu)</div>
                    <div>• Telefon</div>
                    <div>• E-posta</div>
                    <div>• Şehir</div>
                    <div>• İlçe</div>
                    <div>• İletişim Kişisi</div>
                    <div>• Toplam Cihaz Adedi</div>
                    <div>• Pasif Cihaz Adedi</div>
                    <div>• Temsilci</div>
                  </div>
                  <p className="text-blue-800 mt-2 text-xs">
                    💡 Boş satırlar otomatik olarak atlanır.
                  </p>
                </div>
                
                <div>
                  <Label>Excel Dosyası Seçin (.xlsx, .xls)</Label>
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="mt-1"
                  />
                </div>
              </div>
            </DialogContent>
          </Dialog>

        {user?.role === 'admin' && (
          <Button onClick={handleExport} className="bg-white text-blue-600 hover:bg-blue-50 shadow-md flex-1 sm:flex-none min-h-11">
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Excel</span>
          </Button>
        )}

        {user?.role === 'admin' && (
          <Button
            onClick={handleDeleteAll}
            className="bg-white text-red-600 hover:bg-red-50 shadow-md flex-1 sm:flex-none min-h-11"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Tümünü Sil</span>
          </Button>
        )}
      </PageHeader>

      {/* Search and Filters - Geliştirilmiş */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Arama */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Ad, telefon, şehir, yetkili ile ara..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              className="pl-11"
              data-testid="search-prospects-input"
            />
          </div>
          
          {/* Şehir Filtresi */}
          <div>
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger>
                <SelectValue placeholder="Şehir" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Şehirler</SelectItem>
                {uniqueCities.map(city => (
                  <SelectItem key={city} value={city}>
                    {city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Temsilci Filtresi */}
          <div>
            <Select value={selectedRepresentative} onValueChange={setSelectedRepresentative}>
              <SelectTrigger>
                <SelectValue placeholder="Temsilci" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Temsilciler</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.username} value={u.username}>
                    {u.full_name || u.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Sıralama - İkinci Satır */}
        <div className="mt-3">
          <Select value={sortBy} onValueChange={(newValue) => {
            const oldValue = sortBy;
            setSortBy(newValue);
            pushAction({
              undo: () => setSortBy(oldValue)
            });
          }}>
            <SelectTrigger>
              <SelectValue placeholder="Sırala" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date-desc">Tarih (Yeni-Eski)</SelectItem>
              <SelectItem value="date-asc">Tarih (Eski-Yeni)</SelectItem>
              <SelectItem value="name-asc">İsim (A-Z)</SelectItem>
              <SelectItem value="name-desc">İsim (Z-A)</SelectItem>
              <SelectItem value="city-asc">Şehir (A-Z)</SelectItem>
              <SelectItem value="city-desc">Şehir (Z-A)</SelectItem>
              <SelectItem value="representative-asc">Temsilci (A-Z)</SelectItem>
              <SelectItem value="representative-desc">Temsilci (Z-A)</SelectItem>
              <SelectItem value="arvento-desc">Cihaz Sayısı (Çok-Az)</SelectItem>
              <SelectItem value="arvento-asc">Cihaz Sayısı (Az-Çok)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {paginatedCustomers.map((prospect) => (
          <div 
            key={prospect.id} 
            className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#193046] cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(`/prospects/${prospect.id}`)}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">{prospect.name}</h3>
                {prospect.created_at && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                    <Calendar className="w-3 h-3" />
                    <span>{format(new Date(prospect.created_at), 'dd MMM yyyy – HH:mm', { locale: tr })}</span>
                  </div>
                )}
                {prospect.contact_person && (
                  <div className="text-sm text-gray-600 mb-1">Yetkili: {prospect.contact_person}</div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <User className="w-4 h-4" />
                  <span>{prospect.representative || 'Atanmamış'}</span>
                </div>
              </div>
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-bold">
                {prospect.arvento_count || 0}
              </div>
            </div>
            
            <div className="space-y-2 mb-3">
              {prospect.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${prospect.phone}`} className="hover:text-[#193046]" onClick={(e) => e.stopPropagation()}>{prospect.phone}</a>
                </div>
              )}
              {prospect.email && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${prospect.email}`} className="hover:text-[#193046]" onClick={(e) => e.stopPropagation()}>{prospect.email}</a>
                </div>
              )}
              {prospect.city && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{prospect.city}{prospect.district ? ` / ${prospect.district}` : ''}</span>
                </div>
              )}
            </div>

            {user?.role === 'admin' && (
              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCustomer(prospect.id);
                  }}
                  className="min-h-11 px-4"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead className="progo-gradient text-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap">Tarih</th>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap">Müşteri Adayı Adı</th>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap">Yetkili</th>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap">Telefon</th>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap">Şehir</th>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap">Temsilci</th>
                <th className="px-4 py-3 text-center text-sm whitespace-nowrap">Cihaz</th>
                <th className="px-4 py-3 text-center text-sm whitespace-nowrap">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedCustomers.map((prospect) => (
                <tr 
                  key={prospect.id} 
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/prospects/${prospect.id}`)}
                >
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-3 h-3 text-gray-400" />
                      <span>{prospect.created_at ? format(new Date(prospect.created_at), 'dd MMM yyyy – HH:mm', { locale: tr }) : '-'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-sm">{prospect.name}</td>
                  <td className="px-4 py-3 text-sm">{prospect.contact_person || '-'}</td>
                  <td className="px-4 py-3 text-sm">{prospect.phone || '-'}</td>
                  <td className="px-4 py-3 text-sm">{prospect.city || '-'}</td>
                  <td className="px-4 py-3 text-sm">{prospect.representative || '-'}</td>
                  <td className="px-4 py-3 text-center text-sm">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                      {prospect.device_count || 0}
                    </span>
                  </td>
                  {user?.role === 'admin' && (
                    <td className="px-4 py-3 text-center text-sm">
                      <div className="flex justify-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCustomer(prospect.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  )}
                  {user?.role !== 'admin' && <td></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {filteredCustomers.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-700">
            Gösterilen: {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredCustomers.length)} / {filteredCustomers.length}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
              className="min-h-10"
            >
              Önceki
            </Button>
            <span className="px-4 py-2 text-sm font-medium">
              {currentPage} / {Math.ceil(filteredCustomers.length / itemsPerPage)}
            </span>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === Math.ceil(filteredCustomers.length / itemsPerPage)}
              variant="outline"
              size="sm"
              className="min-h-10"
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;